<template>
  <PInputNumber
    ref="input"
    v-bind="$attrs"
    type="tel"
    :placeholder="($attrs.placeholder as string | undefined) ?? 'Telefoonnummer'"
    :model-value="userPart"
    :format="false"
    :increment-on-arrow-keys="false"
    :max-decimals="15"
    :max-leading-digits="15"
    natural-numbers-only
    allow-zero
    :class="slots.leading ? '[&_input]:ps-40!' : '[&_input]:ps-28!'"
    @update:model-value="userPart = $event"
    @input="onInput"
  >
    <template #leading="slotProps">
      <div class="flex h-full items-center gap-2">
        <slot name="leading" v-bind="slotProps" />
        <PSelect
          v-model="selectedCountryCode"
          :items="countryCodes"
          value-key="value"
          label-key="text"
          aria-label="Landcode"
          :disabled="$attrs.disabled as boolean"
          class="pointer-events-auto w-16! shrink-0"
          @update:model-value="onInput"
        />
        <span class="text-sm">{{ selectedCountryCode }}</span>
      </div>
    </template>
    <template v-if="slots.trailing" #trailing="slotProps">
      <slot name="trailing" v-bind="slotProps" />
    </template>
  </PInputNumber>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, watch } from "vue"
import PInputNumber from "./PInputNumber.vue"
import PSelect from "./PSelect.vue"
import type { PInputSlots } from "./input"

const props = defineProps<{ modelValue?: string | null }>()
const emit = defineEmits<{
  "update:modelValue": [value: string | null]
  input: []
}>()
defineOptions({ inheritAttrs: false })
const slots = defineSlots<Pick<PInputSlots, "leading" | "trailing">>()

const countryCodes = [
  { text: "NL", value: "+31" },
  { text: "BE", value: "+32" },
  { text: "FR", value: "+33" },
  { text: "PL", value: "+48" },
  { text: "DE", value: "+49" },
  { text: "LU", value: "+352" }
]
const selectedCountryCode = ref(countryCodes[0].value)
const userPart = ref("")

function onInput() {
  emit("update:modelValue", userPart.value ? selectedCountryCode.value + userPart.value : null)
  emit("input")
}

watch(
  () => props.modelValue,
  (value) => {
    if (!value) {
      userPart.value = ""
      return
    }
    const country = countryCodes.find(({ value: prefix }) => value.startsWith(prefix))
    if (country) {
      selectedCountryCode.value = country.value
      userPart.value = value.slice(country.value.length)
    }
  },
  { immediate: true }
)

const input = useTemplateRef<InstanceType<typeof PInputNumber>>("input")
defineExpose({
  focus: () => input.value?.focus(),
  select: () => input.value?.select()
})
</script>
